import { SystemEntity } from "@erp/shared-types";
import type { SocketType } from "../../types";
import {
  buildDynamicSockets,
  effectiveSocketIdVersion,
  supportsSocketIdV2,
} from "./dynamicSocketId";
import type { DynamicSocketDefinition } from "./socketDefinition";
import { configEntryList, prettify } from "./socketDefinition";

/**
 * Socket type for arrays of CORE_DOCUMENT entity refs. Used by
 * split_document_v2 per-classification sockets so the fanout engine + the
 * editor know exactly what kind of array flows through.
 */
const ARRAY_OF_CORE_DOCUMENT_REFS: SocketType =
  `array<entity_reference:${SystemEntity.CORE_DOCUMENT}>` as SocketType;

/** Socket ids reserved by `split_document_v2`; cannot be used as user classification ids. */
export const SPLIT_DOCUMENT_V2_RESERVED_SOCKET_IDS = ["all_documents"] as const;

/**
 * Generate output sockets for the split_document_v2 step.
 *
 * One array socket per configured classification (id = classification.id),
 * plus a reserved `all_documents` socket that always exists carrying rich
 * `{ document, classificationId, classificationType, confidence? }` objects.
 *
 * With no classifications configured, returns just the reserved `all_documents`
 * socket so the user always sees the catch-all output even before adding any.
 */
export interface ResolvedSplitDocumentV2OutputSockets {
  sockets: DynamicSocketDefinition[];
  /** Output socket id for each configured classification id. */
  socketIdByClassificationId: Map<string, string>;
}

export function resolveSplitDocumentV2OutputSockets(
  config: Record<string, unknown>,
): ResolvedSplitDocumentV2OutputSockets {
  const classifications = configEntryList<{ id?: unknown }>(
    config.customClassifications,
  );

  const usable = classifications
    .map((c) => c.id)
    // The id shape is a config rule, not a collision rule, so it applies at
    // every version.
    .filter(
      (id): id is string =>
        typeof id === "string" && /^[a-z][a-z0-9_]*$/.test(id),
    );

  const { sockets: perClassificationSockets, assignedIds } =
    buildDynamicSockets({
      reserved: SPLIT_DOCUMENT_V2_RESERVED_SOCKET_IDS,
      version: effectiveSocketIdVersion("split_document_v2", config),
      markedFallback: supportsSocketIdV2("split_document_v2"),
      entries: usable.map((id) => ({
        name: id,
        socket: {
          name: prettify(id),
          type: ARRAY_OF_CORE_DOCUMENT_REFS,
          required: false,
          description: `Child documents classified as "${id}"`,
        },
      })),
    });

  const socketIdByClassificationId = new Map<string, string>();
  usable.forEach((id, index) => {
    const assigned = assignedIds[index];
    if (assigned !== null) socketIdByClassificationId.set(id, assigned);
  });

  return {
    sockets: [
      ...perClassificationSockets,
      {
        id: "all_documents",
        name: "All Documents",
        // Rich objects { document, classificationId, classificationType, confidence? }
        // so use array<json> rather than array<entity_reference>.
        type: "array<json>" as SocketType,
        required: false,
        description: "All child documents with their classification tags",
      },
    ],
    socketIdByClassificationId,
  };
}

export function generateSplitDocumentV2OutputSockets(
  config: Record<string, unknown>,
): DynamicSocketDefinition[] {
  return resolveSplitDocumentV2OutputSockets(config).sockets;
}
